import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BackgroundShapes from '../common/BackgroundShapes';

interface RecentSignup {
  name: string;
  email: string;
  enrolledAt: string;
  onboarded: boolean;
}

interface LoginActivity {
  name: string;
  lastLogin: string | null;
  enrolledAt: string;
  inactive: boolean;
}

interface WelcomeUser {
  name: string;
  email: string;
  enrolledAt?: string;
}

interface SuggestionRecord {
  nameA: string;
  nameB: string;
  emailsSent: number;
  stopped: boolean;
  lastSentAt: string | null;
}

interface SuggestionPair {
  nameA: string;
  nameB: string;
  reason?: string;
  daysLeft?: number;
}

interface HealthData {
  generatedAt: string;
  data: {
    enrollment: {
      total: number;
      onboarded: number;
      notOnboarded: number;
      recentSignups: RecentSignup[];
    };
    loginActivity: LoginActivity[];
    welcomeEmail: {
      sent: number;
      pending: WelcomeUser[];
      notReady: WelcomeUser[];
    };
    suggestionHistory: SuggestionRecord[];
    pendingPairs: {
      readyToSend: SuggestionPair[];
      waitingForWindow: SuggestionPair[];
      maxedOut: SuggestionPair[];
    };
  };
}

function fmt(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toISOString().slice(0, 10);
}

function daysAgo(dateStr: string | null) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (d === 0) return 'today';
  return `${d}d ago`;
}

const AdminHealthPage: React.FC = () => {
  const navigate = useNavigate();
  const [health, setHealth] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const res = await fetch(`${apiUrl}/admin/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Failed to load');
      setHealth(json);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const d = health?.data;

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      <div className="container mx-auto px-4 py-8 relative z-10">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-coffee/60 text-sm mb-1 hover:text-coffee transition-colors"
            >
              ← Admin Dashboard
            </button>
            <h1 className="text-3xl font-bold text-coffee">User Health</h1>
          </div>
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="bg-terracotta text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm disabled:opacity-50"
          >
            {loading ? 'Loading…' : health ? 'Refresh' : 'Load Report'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 mb-6 text-red-700">
            ❌ {error}
          </div>
        )}

        {!health && !loading && !error && (
          <div className="text-center text-coffee/50 py-24">
            Click <strong>Load Report</strong> to fetch live data from prod.
          </div>
        )}

        {loading && (
          <div className="text-center text-coffee/50 py-24">
            Building report… (this may take ~10s while computing suggestion pairs)
          </div>
        )}

        {d && (
          <>
            <p className="text-coffee/40 text-xs mb-6">
              Generated at {new Date(health.generatedAt).toLocaleString()}
            </p>

            {/* ── Enrollment ── */}
            <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm mb-6">
              <h2 className="text-lg font-bold text-coffee mb-4">📋 Enrollment</h2>
              <div className="flex gap-8 mb-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-coffee">{d.enrollment.total}</div>
                  <div className="text-coffee/60 text-sm">Total signups</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-coffee">{d.enrollment.onboarded}</div>
                  <div className="text-coffee/60 text-sm">Onboarded</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-coffee">{d.enrollment.notOnboarded}</div>
                  <div className="text-coffee/60 text-sm">Not onboarded</div>
                </div>
              </div>
              {d.enrollment.recentSignups.length > 0 && (
                <>
                  <p className="text-coffee/60 text-sm font-semibold mb-2">Recent signups (14 days)</p>
                  <div className="space-y-1">
                    {d.enrollment.recentSignups.map((u, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-coffee">{u.name}</span>
                        <span className="text-coffee/50">{fmt(u.enrolledAt)}</span>
                        <span>{u.onboarded ? '✅ onboarded' : '⏳ not onboarded'}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* ── Login Activity + Welcome Email ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

              {/* Login Activity */}
              <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-coffee mb-4">🔐 Login Activity</h2>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {d.loginActivity.map((u, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className={`${u.inactive ? 'text-coffee/50' : 'text-coffee'} truncate max-w-[140px]`}>
                        {u.inactive ? '⚠️ ' : ''}{u.name}
                      </span>
                      <span className="text-coffee/50">{daysAgo(u.lastLogin)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-coffee/40 text-xs mt-3">⚠️ = no login or inactive &gt;14 days</p>
              </div>

              {/* Welcome Email */}
              <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-coffee mb-4">✉️ Welcome Email</h2>
                <div className="flex gap-6 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-coffee">{d.welcomeEmail.sent}</div>
                    <div className="text-coffee/60 text-sm">Sent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-terracotta">{d.welcomeEmail.pending.length}</div>
                    <div className="text-coffee/60 text-sm">Pending</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-coffee/50">{d.welcomeEmail.notReady.length}</div>
                    <div className="text-coffee/60 text-sm">Not ready</div>
                  </div>
                </div>
                {d.welcomeEmail.pending.length > 0 && (
                  <div className="mb-3">
                    <p className="text-terracotta text-xs font-semibold mb-1">⚠️ Should receive welcome now:</p>
                    {d.welcomeEmail.pending.map((u, i) => (
                      <p key={i} className="text-sm text-coffee">{u.name} — {u.email}</p>
                    ))}
                  </div>
                )}
                {d.welcomeEmail.notReady.length > 0 && (
                  <div>
                    <p className="text-coffee/60 text-xs font-semibold mb-1">⏳ Onboarding incomplete:</p>
                    {d.welcomeEmail.notReady.map((u, i) => (
                      <p key={i} className="text-sm text-coffee/70">{u.name} — enrolled {fmt(u.enrolledAt || null)}</p>
                    ))}
                  </div>
                )}
                {d.welcomeEmail.pending.length === 0 && d.welcomeEmail.notReady.length === 0 && (
                  <p className="text-sm text-coffee/50">✅ All clear</p>
                )}
              </div>
            </div>

            {/* ── Suggestion History + Pending Pairs ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Suggestion History */}
              <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-coffee mb-1">💌 Suggestion History</h2>
                <p className="text-coffee/50 text-sm mb-4">{d.suggestionHistory.length} pairs total</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {d.suggestionHistory.map((n, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-coffee truncate max-w-[180px]">{n.nameA} ↔ {n.nameB}</span>
                      <span className="text-coffee/50 text-xs">
                        {n.emailsSent} sent{n.stopped ? ' · stopped' : ''}{n.lastSentAt ? ` · ${fmt(n.lastSentAt)}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending Pairs */}
              <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-coffee mb-4">📬 Pending Pairs</h2>
                <div className="flex gap-6 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{d.pendingPairs.readyToSend.length}</div>
                    <div className="text-coffee/60 text-sm">Ready</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-coffee">{d.pendingPairs.waitingForWindow.length}</div>
                    <div className="text-coffee/60 text-sm">Waiting</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-coffee/40">{d.pendingPairs.maxedOut.length}</div>
                    <div className="text-coffee/60 text-sm">Maxed</div>
                  </div>
                </div>
                {d.pendingPairs.readyToSend.length > 0 && (
                  <div className="mb-3">
                    <p className="text-green-600 text-xs font-semibold mb-1">🟢 Ready to send:</p>
                    {d.pendingPairs.readyToSend.map((p, i) => (
                      <p key={i} className="text-sm text-coffee">{p.nameA} ↔ {p.nameB}</p>
                    ))}
                  </div>
                )}
                {d.pendingPairs.waitingForWindow.length > 0 && (
                  <div>
                    <p className="text-coffee/60 text-xs font-semibold mb-1">⏳ Waiting 7-day window:</p>
                    {d.pendingPairs.waitingForWindow.map((p, i) => (
                      <p key={i} className="text-sm text-coffee/70">{p.nameA} ↔ {p.nameB} — {p.daysLeft}d left</p>
                    ))}
                  </div>
                )}
                {d.pendingPairs.readyToSend.length === 0 && d.pendingPairs.waitingForWindow.length === 0 && d.pendingPairs.maxedOut.length === 0 && (
                  <p className="text-sm text-coffee/50">No active pairs</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminHealthPage;
